import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workorders, documents, contractors, predictions as predictionsApi, buildings as buildingsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  requested:   'bg-yellow-100 text-yellow-800',
  quoted:      'bg-blue-100 text-blue-800',
  approved:    'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed:   'bg-green-100 text-green-800',
  cancelled:   'bg-gray-100 text-gray-600',
};

const WO_PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

function StatCard({ label, value, sub, color, to }) {
  const inner = (
    <div className={`rounded-xl p-4 sm:p-5 border ${color} hover:shadow-md transition-all active:scale-[0.97]`}>
      <p className="text-xs sm:text-sm font-medium text-gray-500">{label}</p>
      <p className="text-2xl sm:text-3xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

const PRIORITY_COLORS = {
  high:   'text-red-600 bg-red-50',
  medium: 'text-orange-600 bg-orange-50',
  low:    'text-blue-600 bg-blue-50',
};

function OnboardingChecklist({ buildingCount, contractorCount, woCount, docCount }) {
  const STORAGE_KEY = 'stratahub_onboarding_dismissed';
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(STORAGE_KEY));

  const steps = [
    { label: 'Add your first building',   done: buildingCount > 0,    to: '/buildings',   cta: 'Add Building' },
    { label: 'Add contractors',            done: contractorCount > 0,  to: '/contractors', cta: 'Add Contractor' },
    { label: 'Create a work order',        done: woCount > 0,          to: '/workorders',  cta: 'Create Work Order' },
    { label: 'Upload a document',          done: docCount > 0,         to: '/documents',   cta: 'Upload Document' },
    { label: 'Try AI Compliance Checker',  done: false,                to: '/compliance',  cta: 'Try It' },
  ];

  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;

  if (dismissed || allDone) return null;

  const pct = Math.round((completed / steps.length) * 100);

  return (
    <div className="mb-8 bg-white rounded-xl border border-blue-200 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div>
          <h2 className="font-semibold text-white">Get started with StrataPulse</h2>
          <p className="text-blue-100 text-xs mt-0.5">{completed} of {steps.length} steps complete</p>
        </div>
        <button
          onClick={() => { localStorage.setItem(STORAGE_KEY, '1'); setDismissed(true); }}
          className="text-blue-200 hover:text-white text-xs underline">
          Dismiss
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-blue-100">
        <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <div className="divide-y divide-gray-100">
        {steps.map((step) => (
          <div key={step.label} className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                step.done ? 'bg-green-500' : 'bg-gray-200'
              }`}>
                {step.done ? (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className="w-2 h-2 rounded-full bg-gray-400" />
                )}
              </div>
              <span className={`text-sm ${step.done ? 'text-gray-400 line-through' : 'text-gray-700 font-medium'}`}>
                {step.label}
              </span>
            </div>
            {!step.done && (
              <Link to={step.to}
                className="text-xs text-blue-600 hover:text-blue-700 font-semibold shrink-0">
                {step.cta} →
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentDocs, setRecentDocs] = useState([]);
  const [contractorCount, setContractorCount] = useState(0);
  const [buildingCount, setBuildingCount] = useState(0);
  const [topPredictions, setTopPredictions] = useState([]);
  const [predInsights, setPredInsights] = useState(null);

  useEffect(() => {
    workorders.stats().then((r) => setStats(r.data)).catch(() => {});
    workorders.list().then((r) => setRecentOrders(r.data.slice(0, 5))).catch(() => {});
    documents.list().then((r) => setRecentDocs(r.data.slice(0, 5))).catch(() => {});
    contractors.list().then((r) => setContractorCount(r.data.length)).catch(() => {});
    buildingsApi.list().then((r) => setBuildingCount(r.data.length)).catch(() => {});
    predictionsApi.list({ status: 'active', limit: 3 })
      .then((r) => setTopPredictions(r.data)).catch(() => {});
    predictionsApi.insights()
      .then((r) => setPredInsights(r.data)).catch(() => {});
  }, []);

  const totalWo = stats ? (stats.requested + stats.quoted + stats.approved + stats.in_progress + stats.completed) : 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name} 👋
        </h1>
        <p className="text-gray-500 mt-1">Here's what's happening across your buildings.</p>
      </div>

      <OnboardingChecklist
        buildingCount={buildingCount}
        contractorCount={contractorCount}
        woCount={totalWo}
        docCount={recentDocs.length}
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Pending Orders" to="/workorders"
          value={stats ? (stats.requested + stats.quoted + stats.approved + stats.in_progress) : '—'}
          sub="Active work orders"
          color="bg-white border-gray-200"
        />
        <StatCard
          label="Completed" to="/workorders"
          value={stats?.completed ?? '—'}
          sub="Work orders done"
          color="bg-white border-gray-200"
        />
        <StatCard
          label="Contractors" to="/contractors"
          value={contractorCount}
          sub="In your database"
          color="bg-white border-gray-200"
        />
        <StatCard
          label="Documents" to="/documents"
          value={recentDocs.length > 0 ? recentDocs.length + '+' : '—'}
          sub="Uploaded files"
          color="bg-white border-gray-200"
        />
      </div>

      {/* 🔮 AI Predictions widget */}
      {(topPredictions.length > 0 || (predInsights && predInsights.prevented_issues > 0)) && (
        <div className="mb-8 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl border border-violet-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-violet-100">
            <div className="flex items-center gap-2">
              <span className="text-lg">🔮</span>
              <h2 className="font-semibold text-gray-900">AI Predictions</h2>
              {predInsights?.high_priority_count > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  {predInsights.high_priority_count} urgent
                </span>
              )}
            </div>
            <Link to="/predictions" className="text-sm text-violet-600 hover:underline font-medium">
              View all →
            </Link>
          </div>

          {topPredictions.length === 0 ? (
            <div className="px-5 py-6 text-center">
              <p className="text-sm text-gray-500">
                No active predictions.{' '}
                <Link to="/predictions" className="text-violet-600 hover:underline">
                  Run AI analysis →
                </Link>
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-violet-100">
              {topPredictions.map((pred) => {
                const days = pred.predicted_days;
                const daysLabel = days === 0 ? 'Overdue'
                  : days <= 7  ? `${days}d`
                  : days <= 30 ? `~${days}d`
                  : `~${Math.round(days / 7)}w`;
                const urgentColor = days <= 7 ? 'text-red-600' : days <= 21 ? 'text-orange-600' : 'text-gray-600';
                return (
                  <li key={pred.id} className="px-5 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{pred.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {pred.building_name} · {pred.confidence}% confidence
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-bold ${urgentColor}`}>{daysLabel}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[pred.priority] || ''}`}>
                        {pred.priority}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {predInsights && predInsights.prevented_issues > 0 && (
            <div className="px-5 py-3 bg-violet-50/60 border-t border-violet-100 flex gap-4 text-xs text-violet-700">
              <span>✅ <strong>{predInsights.prevented_issues}</strong> issues prevented</span>
              {predInsights.estimated_savings > 0 && (
                <span>💰 <strong>${Math.round(predInsights.estimated_savings).toLocaleString()}</strong> est. saved</span>
              )}
              {predInsights.accuracy_percentage !== null && (
                <span>🎯 <strong>{predInsights.accuracy_percentage}%</strong> accurate</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent work orders */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h2 className="font-semibold text-gray-900">Recent Work Orders</h2>
            <Link to="/workorders" className="text-sm text-blue-600 hover:underline">View all</Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400 text-sm">
              No work orders yet.{' '}
              <Link to="/workorders" className="text-blue-600 hover:underline">Create one →</Link>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentOrders.map((o) => (
                <li key={o.id} className="px-5 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{o.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{o.building_name}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${WO_PRIORITY_COLORS[o.priority]}`}>
                      {o.priority}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[o.status]}`}>
                      {o.status.replace('_', ' ')}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent documents */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h2 className="font-semibold text-gray-900">Recent Documents</h2>
            <Link to="/documents" className="text-sm text-blue-600 hover:underline">View all</Link>
          </div>
          {recentDocs.length === 0 ? (
            <div className="px-5 py-8 text-center text-gray-400 text-sm">
              No documents yet.{' '}
              <Link to="/documents" className="text-blue-600 hover:underline">Upload one →</Link>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {recentDocs.map((d) => (
                <li key={d.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.name}</p>
                    <p className="text-xs text-gray-400">{d.category} · {d.building_name || 'General'}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-6">
        <h2 className="font-semibold text-gray-900 mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/voice-email', label: 'Voice Email', bg: 'bg-purple-50 hover:bg-purple-100 active:bg-purple-200 text-purple-700', emoji: '🎙️' },
            { to: '/workorders', label: 'New Work Order', bg: 'bg-blue-50 hover:bg-blue-100 active:bg-blue-200 text-blue-700', emoji: '📋' },
            { to: '/contractors', label: 'Add Contractor', bg: 'bg-green-50 hover:bg-green-100 active:bg-green-200 text-green-700', emoji: '👷' },
            { to: '/documents', label: 'Upload Doc', bg: 'bg-orange-50 hover:bg-orange-100 active:bg-orange-200 text-orange-700', emoji: '📄' },
          ].map(({ to, label, bg, emoji }) => (
            <Link key={to} to={to}
              className={`${bg} rounded-xl p-4 text-sm font-semibold text-center transition-all active:scale-95 flex flex-col items-center gap-1`}
              style={{ minHeight: 72 }}>
              <span className="text-xl">{emoji}</span>
              {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
